'use client';

import React, { FC, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel';

type PropType = {
  options?: EmblaOptionsType;
  children: React.ReactNode;
  autoplay?: boolean;
  autoplayIntervalMs?: number;
  pauseOnHover?: boolean;
  pauseOnInteraction?: boolean;
};

const Carousel: FC<PropType> = ({
  options,
  children,
  autoplay = false,
  autoplayIntervalMs = 5000,
  pauseOnHover = true,
  pauseOnInteraction = true,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const autoplayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const stopAutoplay = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    if (!autoplay || !emblaApi) return;
    stopAutoplay();
    autoplayTimerRef.current = setInterval(() => {
      emblaApi.scrollNext();
    }, autoplayIntervalMs);
  }, [autoplay, emblaApi, autoplayIntervalMs, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi || !autoplay) return;
    startAutoplay();
    return () => {
      stopAutoplay();
    };
  }, [emblaApi, autoplay, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi || !autoplay || !pauseOnInteraction) return;
    const handlePointerDown = () => stopAutoplay();
    emblaApi.on('pointerDown', handlePointerDown);
    return () => {
      emblaApi.off('pointerDown', handlePointerDown);
    };
  }, [emblaApi, autoplay, pauseOnInteraction, stopAutoplay]);

  return (
    <div
      className="relative"
      onMouseEnter={pauseOnHover ? stopAutoplay : undefined}
      onMouseLeave={pauseOnHover ? startAutoplay : undefined}
    >
      {/* Viewport do Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        {/* Contêiner dos Slides */}
        <div className="flex">
          {children}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        className="absolute top-1/2 left-4 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-200 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
      </button>
      <button
        onClick={scrollNext}
        className="absolute top-1/2 right-4 -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-200 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
      </button>
    </div>
  );
};

export default Carousel;